import { DOCUMENT } from '@angular/common';
import {
    Inject,
    Injectable,
    OnDestroy,
} from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    AuthService,
    StorageService,
} from '@services';

import {
    fromEvent,
    merge,
    Subject,
} from 'rxjs';
import {
    debounceTime,
    startWith,
    takeUntil,
    tap,
    throttleTime,
} from 'rxjs/operators';

const EVENT_OPTIONS = { capture: true };
export const INACTIVITY_THROTTLE_TIME = 250;

const LS_INACTIVE_TIMER_KEY = 'LS_INACTIVE_TIMER';

@Injectable()
export class InactivityTimer implements OnDestroy {
    private _timeout: number;
    private _destroyed$ = new Subject();
    private _timerKey: string;

    constructor(
        auth: AuthService,
        appState: AppStateFacade,
        storage: StorageService,
        @Inject(DOCUMENT)
        private _document: Document,
    ) {
        this._timerKey = `${LS_INACTIVE_TIMER_KEY}-${appState.authConfig.buildVersion}`;
        this._timeout = appState.settings.get(Setting.cookieExpireMinutes)! * 60 * 1000;

        if (isNaN(this._timeout)) {
            return;
        }

        const window = this._document.defaultView as Window;

        const userEvents$ = merge(
            fromEvent(window, 'mousemove', EVENT_OPTIONS),
            fromEvent(window, 'touchmove', EVENT_OPTIONS),
            fromEvent(window, 'mousedown', EVENT_OPTIONS),
            fromEvent(window, 'click', EVENT_OPTIONS),
            fromEvent(window, 'scroll', EVENT_OPTIONS),
            fromEvent(window, 'keypress', EVENT_OPTIONS),
            fromEvent(window, 'keydown', EVENT_OPTIONS),
            fromEvent(window, 'keyup', EVENT_OPTIONS),
        ).pipe(
            startWith(void 0),
            throttleTime(INACTIVITY_THROTTLE_TIME),
            tap(() => storage.set(this._timerKey, Date.now())),
        );

        const otherTabActivity$ = storage.watch(this._timerKey);

        merge(
            userEvents$,
            otherTabActivity$,
        )
            .pipe(
                debounceTime(this._timeout),
                takeUntil(this._destroyed$),
            )
            .subscribe(() => auth.logout());
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }
}
